// Import necessary hooks and libraries
import {createContext, ReactNode, use, useCallback, useEffect, useMemo, useState} from "react";
import useWebSocket, {ReadyState} from "react-use-websocket";
import {WSS_URL} from "@/lib/constants";
import {useSession} from "@/lib/auth/auth";
import {chat} from "@/lib/proto";
import {useGlobalSearchParams} from "expo-router";
import {v7 as uuidv7} from "uuid";
import Chat = chat.Chat;
import Request = chat.Request;
import Response = chat.Response;
import ChatMessage = chat.ChatMessage;
import LoginRequest = chat.LoginRequest;
import ListChatsRequest = chat.ListChatsRequest;
import ReplayRequest = chat.ReplayRequest;
import SendChatMessageRequest = chat.SendChatMessageRequest;
import Avatar = chat.Avatar;
import ProtoError = chat.Error;
import MessageReadRequest = chat.MessageReadRequest;

// Create a context for chat messages
const ChatContext = createContext<ChatProviderContextProps>({
    chats: new Map(),
    chatConnected: false,
    chatBusy: false,
    chatError: null,
    unreadChatMessages: undefined,
    unreadMessagesForChat: () => {
    },
    sendChatMessage: () => {
    },
    subscribeToChat: () => {
    },
    markMessageAsRead: () => {
    },
});

type ChatProviderProps = {
    children?: ReactNode,
}

type ChatProviderContextProps = {
    chats: Map<string, ChatRecord>,
    currentChat?: ChatRecord,
    chatConnected: boolean,
    chatBusy: boolean,
    unreadChatMessages: number | undefined,
    unreadMessagesForChat: (chat_id: string) => any,
    chatError: Error | null,
    sendChatMessage: (message: SendChatMessageRequest, callback?: () => any) => any,
    subscribeToChat: (id: string) => any,
    markMessageAsRead: (chat_id: string, message_id: string) => any,
}

export type Reactions = Map<string, Avatar[]>;

interface ChatRecord {
    chat: Chat;
    messages: Map<string, ChatMessage>;
    // maps to message key
    reactions: Map<string, Reactions>;
    pendingMessages: Map<string, SendChatMessageRequest>;
}

// Define a custom hook to access the chat messages context
export function useChat() {
    const value = use(ChatContext);
    if (!value) {
        throw new Error('useChat must be wrapped in a <ChatProvider />');
    }
    return value;
}

// Define the ChatProvider component to provide chat messages context
export const ChatProvider = ({children}: ChatProviderProps) => {
    const {session} = useSession();
    const {chat_id} = useGlobalSearchParams();
    const [chats, setChats] = useState<Map<string, ChatRecord>>(new Map());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [messageReceivedCallbacks, setMessageReceivedCallbacks] = useState<Map<string, () => any>>(new Map());

    // Initialize the WebSocket connection and retrieve necessary properties
    const {
        sendMessage: sM,
        lastMessage,
        readyState,
    } = useWebSocket(WSS_URL + "/chat/v1", {
        disableJson: true,
        shouldReconnect: () => true,
        queryParams: {}
    });

    const chatConnected = readyState === ReadyState.OPEN;

    const sendRequest = useCallback(
        (request: Request) => {
            setBusy(true);
            const payload = Request.encode(request).finish();
            setError(null);
            sM(payload);
            setBusy(false);
        },
        [sM],
    );

    // Handshake when initializing connection
    useEffect(() => {
        if (readyState !== ReadyState.OPEN || !session) {
            return;
        }
        sendRequest(Request.create({
            authenticate: LoginRequest.create({
                sessionId: session?.session_id,
            })
        }));
    }, [sendRequest, readyState, session]);

    const sendChatMessage = useCallback(
        (message: SendChatMessageRequest, callback?: () => any) => {
            if (message.text.length + message.attachments.length + message.reaction.length === 0) {
                return;
            }
            message.clientMessageId = uuidv7();
            const chat = chats.get(message.chatId!);
            if (chat !== undefined) {
                chat.pendingMessages = new Map(chat.pendingMessages.set(message.clientMessageId, message));
                setChats(new Map(chats.set(message.chatId!, chat)));
            }
            const request = Request.create({
                sendMessage: message,
            });
            if (callback !== undefined) {
                // console.debug("Registering callback for message", message.clientMessageId);
                setMessageReceivedCallbacks(
                    new Map(messageReceivedCallbacks.set(message.clientMessageId, callback))
                );
            }
            return sendRequest(request);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const appendChat = useCallback((c: Chat) => {
        if (!c.id) {
            return;
        }
        // console.debug("Got chat", c.id);
        const chatRecord: ChatRecord = chats.get(c.id) || {
            chat: Chat.create(),
            messages: new Map(),
            reactions: new Map(),
            pendingMessages: new Map(),
        };
        chatRecord.chat = c;
        setChats(new Map(chats.set(c.id, chatRecord)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const appendMessage = useCallback((message: ChatMessage) => {
        if (!message.chatId || !message.messageId || !message.sender || !message.sender.id) {
            return;
        }
        // console.debug(new Date(), `Got message ${message.messageId} in chat ${message.chatId}`, message);
        let chat = chats.get(message.chatId);
        if (chat === undefined) {
            console.warn(`Ignoring message`, message.messageId, `in unknown chat`, message.chatId);
            return;
        }

        // round-trip complete!
        const pendingMessages = new Map(chat.pendingMessages);
        pendingMessages.delete(message.clientMessageId);
        chat.pendingMessages = pendingMessages;

        if (message.reaction.length > 0) {
            const inReplyTo = chat.messages.get(message.inReplyTo);
            if (inReplyTo === undefined) {
                return;
            }
            const reactions = new Map(chat.reactions);
            const reactionRecords: Reactions = new Map(reactions.get(inReplyTo.messageId)) || new Map();
            const reactionList = [...reactionRecords.get(message.reaction) || []];
            const previousIndex = reactionList.findIndex(avatar => message.sender!.id === avatar.id);
            // one more of a previous reaction of the same kind will delete it.
            if (previousIndex !== -1) {
                reactionList.splice(previousIndex, 1);
            } else {
                reactionList.push(message.sender as Avatar);
            }
            reactionRecords.set(message.reaction, reactionList);
            reactions.set(inReplyTo.messageId, reactionRecords);
            chat.reactions = reactions;
        } else {
            const messages = new Map(chat.messages);
            messages.delete(message.messageId);
            messages.set(message.messageId, message);
            chat.messages = messages;
            if (message.sender.id === session?.user_id) {
                // mark own messages as seen immediately
                chat.chat.lastReadMessage = message.messageId;
            }
            chat.chat.lastActivity = message.receivedAt;
        }

        setChats(new Map(chats.set(message.chatId, chat)));

        const callback = messageReceivedCallbacks.get(message.clientMessageId);
        if (callback !== undefined) {
            // console.debug("Trigger callback for message", message.clientMessageId);
            messageReceivedCallbacks.delete(message.clientMessageId);
            setMessageReceivedCallbacks(new Map(messageReceivedCallbacks));
            callback();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleError = useCallback((msg: ProtoError) => {
        setError(new Error(msg.message));
    }, []);

    // Handle the incoming WebSocket messages
    useEffect(() => {
        if (!lastMessage || !lastMessage.data) {
            return;
        }
        const blob = lastMessage.data as Blob;
        blob.bytes().then((payload) => {
            const msg = Response.decode(payload)
            if (!msg) {
                return;
            }
            if (msg.welcome) {
                sendRequest(Request.create({
                    listChats: ListChatsRequest.create(),
                }));
                sendRequest(Request.create({
                    replay: ReplayRequest.create(), // todo: point in time
                }));
            } else if (msg.message) {
                appendMessage(msg.message as ChatMessage);
            } else if (msg.chat) {
                appendChat(msg.chat as Chat);
            } else if (msg.error) {
                handleError(msg.error as ProtoError);
            }
        });
    }, [lastMessage, appendMessage, appendChat, sendRequest, handleError]);

    const currentChat = useMemo(() => {
        return chats.get(chat_id?.toString());
    }, [chat_id, chats]);

    const subscribeToChat = useCallback((chat_id: string) => {
        sendRequest(Request.create({
            replay: ReplayRequest.create({
                chatId: chat_id,
            })
        }))
    }, [sendRequest]);

    const unreadMessagesForChat = useCallback((chat_id: string) => {
        const record = chats.get(chat_id);
        if (record === undefined) {
            return 0;
        }
        return record.messages.entries()
            .filter(([key, _]) => key > record.chat.lastReadMessage)
            .reduce((acc: number, _: [string, ChatMessage]) => acc + 1, 0);
    }, [chats]);

    const unreadChatMessages = useMemo(() => {
        let total = 0;
        chats.forEach((record) => {
            total += unreadMessagesForChat(record.chat.id);
        })
        return total;
    }, [chats, unreadMessagesForChat])

    const markMessageAsRead = useCallback((chat_id: string, message_id: string) => {
        const record = chats.get(chat_id);
        if (record === undefined) {
            return;
        }
        if (message_id <= record.chat.lastReadMessage) {
            return;
        }
        // console.log("Read message", message_id);
        record.chat.lastReadMessage = message_id;
        sendRequest(Request.create({
            messageRead: MessageReadRequest.create({
                chatId: chat_id,
                messageId: message_id,
            })
        }));
        setChats(new Map(chats.set(chat_id, record)));
    }, [chats, setChats, sendRequest]);

    // Render the ChatContext.Provider component and pass the necessary values
    return (
        <ChatContext.Provider value={{
            chats,
            chatConnected,
            chatBusy: busy,
            chatError: error,
            sendChatMessage,
            subscribeToChat,
            markMessageAsRead,
            unreadMessagesForChat,
            unreadChatMessages,
            currentChat,
        }}>
            {children}
        </ChatContext.Provider>
    );
};